import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";

const MOBILE_LINKS = [
  { label: "Philosophie", path: "/philosophie" },
  { label: "Formations", path: "/formations" },
  { label: "Galerie", path: "/galerie" },
  { label: "L'école", path: "/ecole" },
  { label: "Ambassadeurs", path: "/ambassadeurs" },
  { label: "Témoignages", path: "/temoignages" },
  { label: "Presse", path: "/presse" },
  { label: "Certification", path: "/certification" },
  { label: "Contact", path: "/contact" },
  { label: "FAQ", path: "/faq" },
];

const NAV_GROUPS = [
  { label: "Formations", path: "/formations" },
  {
    label: "L'école",
    id: "ecole",
    items: [
      { label: "Philosophie", path: "/philosophie" },
      { label: "L'école", path: "/ecole" },
      { label: "Certification", path: "/certification" },
      { label: "Ambassadeurs", path: "/ambassadeurs" },
    ],
  },
  { label: "Galerie", path: "/galerie" },
  {
    label: "Médias",
    id: "medias",
    items: [
      { label: "Témoignages", path: "/temoignages" },
      { label: "Presse", path: "/presse" },
      { label: "FAQ", path: "/faq" },
    ],
  },
  { label: "Contact", path: "/contact" },
];

function LangPill({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`flex items-center rounded-full border border-aqua/25 p-0.5 ${compact ? "mx-8 my-4 w-fit" : ""}`}
      data-testid="lang-toggle"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase transition-all duration-300 ${
            lang === l ? "bg-aqua text-abyss" : "text-foam/60 hover:text-foam"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { tr } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo(0, 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-abyss/95 backdrop-blur-xl border-b border-aqua/15"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-nav"
      >
        <nav className="relative mx-auto max-w-7xl px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" onClick={goHome}>
            <Logo />
          </Link>

          <Link
            to="/presse"
            onClick={() => setOpen(false)}
            className="absolute left-1/2 top-1/2 translate-x-[calc(-50%+2cm)] -translate-y-1/2 lg:hidden"
            data-testid="tf1-nav-logo"
          >
            <img src="/media/logo-tf1.jpg" alt="Reportage TF1 — Grands Reportages" className="h-8 w-auto rounded-md shadow-[0_4px_16px_rgba(3,10,22,0.4)]" />
          </Link>

          <div className="hidden lg:flex items-center gap-6 ml-10">
            {NAV_GROUPS.map((g) =>
              g.items ? (
                <div key={g.label} className="relative group" data-testid={`nav-dropdown-${g.id}`}>
                  <button className="flex items-center gap-1 py-4 text-[12px] font-medium tracking-wide text-foam/70 transition-colors duration-300 hover:text-foam">
                    {tr(g.label)}
                    <ChevronDown size={13} className="transition-transform duration-300 group-hover:rotate-180" />
                  </button>
                  <div className="invisible absolute left-0 top-full z-50 w-52 translate-y-1 rounded-2xl border border-aqua/15 bg-abyss/95 p-2 opacity-0 shadow-[0_20px_60px_rgba(3,10,22,0.6)] backdrop-blur-xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {g.items.map((l) => (
                      <Link
                        key={l.path}
                        to={l.path}
                        data-testid={`nav-link-${l.path.slice(1)}`}
                        onClick={() => setOpen(false)}
                        className="block rounded-xl px-4 py-2.5 text-[12px] font-medium tracking-wide text-foam/70 transition-colors duration-200 hover:bg-aqua/10 hover:text-foam"
                      >
                        {tr(l.label)}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={g.label}
                  to={g.path}
                  data-testid={`nav-link-${g.path.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className="text-[12px] font-medium tracking-wide text-foam/70 hover:text-foam transition-colors duration-300"
                >
                  {tr(g.label)}
                </Link>
              )
            )}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LangPill />
            {user?.role === "admin" && (
              <Link
                to="/espace-ecole"
                data-testid="nav-admin-link"
                className="text-[12px] font-medium text-aqua hover:text-foam transition-colors"
              >
                {tr("Espace école")}
              </Link>
            )}
            {user ? (
              <Link
                to="/tableau-de-bord"
                data-testid="nav-dashboard-link"
                className="text-[12px] font-medium text-foam/80 hover:text-foam transition-colors"
              >
                {tr("Mon espace")}
              </Link>
            ) : (
              <Link
                to="/connexion"
                data-testid="nav-login-link"
                className="text-[12px] font-medium text-foam/80 hover:text-foam transition-colors"
              >
                {tr("Connexion")}
              </Link>
            )}
            <Link
              to="/formations"
              data-testid="nav-cta"
              className="rounded-full bg-aqua text-abyss text-[12px] font-semibold px-4 py-2.5 hover:bg-foam hover:shadow-[0_0_28px_rgba(56,189,248,0.45)] transition-all duration-300"
            >
              {tr("Réserver une formation")}
            </Link>
          </div>

          <button
            className="lg:hidden text-foam p-2"
            data-testid="nav-toggle"
            onClick={() => setOpen(!open)}
            aria-label={tr("Menu")}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-abyss/95 backdrop-blur-2xl lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="pt-24 px-8 pb-12 flex flex-col">
              <LangPill compact />
              {MOBILE_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                >
                  <Link
                    to={l.path}
                    onClick={() => setOpen(false)}
                    className="block font-serif text-2xl text-foam/90 py-2 border-b border-aqua/10"
                  >
                    {tr(l.label)}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                {user?.role === "admin" && (
                  <Link
                    to="/espace-ecole"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-admin-link"
                    className="rounded-full border border-aqua/40 text-aqua text-center py-3.5 text-sm font-medium"
                  >
                    {tr("Espace école")}
                  </Link>
                )}
                <Link
                  to={user ? "/tableau-de-bord" : "/connexion"}
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-aqua/40 text-foam text-center py-3.5 text-sm font-medium"
                  data-testid="mobile-auth-link"
                >
                  {user ? tr("Mon espace") : tr("Connexion")}
                </Link>
                <Link
                  to="/formations"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-aqua text-abyss text-center py-3.5 text-sm font-semibold"
                  data-testid="mobile-cta"
                >
                  {tr("Réserver une formation")}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
